"use client";

import Image from "next/image";
import { useState, type CSSProperties } from "react";

import { LIST_COVER_PLACEHOLDER_SRC } from "@/components/brand/list-cover-placeholder";
import { CATALOG_HERO_MIN_HEIGHT } from "@/components/host-panel/catalog/catalog-hero-metrics";
import { CalendarIcon } from "@/components/host-panel/panel-icons";
import { SectionLabel } from "@/components/shared/section-label";
import { formatCivilDateInFull } from "@/lib/civil-date";
import { resolveCoverUrl } from "@/lib/host-panel/cover-image";
import type { HostPresentation } from "@/lib/host-panel/presentation";
import { TRANSPARENT_PIXEL } from "@/lib/host-panel/piece-image";
import { IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";
import { isOptimizableImageSrc } from "@/lib/images/is-optimizable-src";
import { cn } from "@/lib/utils";
import { useCatalogHeroParallax } from "./use-catalog-hero-parallax";

/**
 * Hero of the list (handoff §7; v2 §6.1): the list's name, its cover, the welcome message, the
 * date and the place.
 *
 * Read only since v2: "Editar apresentação" left the hero, and the couple, the date, the photo and
 * the message are edited in Dados do Evento. On a phone the name comes **before** the photo, so
 * the list is known at once; on a desktop the words sit left of the 4:5 cover, one viewport tall.
 *
 * Full-bleed like the prototype: `width:100vw` breaks out of the shell's container, and the
 * negative margin recenters it.
 */

const LOCATION_ICON_PATH = "M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z";

export type CatalogHeroProps = {
  eventName: string;
  presentation: HostPresentation;
  isClosed?: boolean;
  /**
   * A house suggestion list instead of a couple's (the design's `sugestao` context, `verSugestao`):
   * its own eyebrow, the title over two lines with the accent in green, the list's description
   * without quotation marks and its count, and no date or place.
   */
  suggestion?: HeroSuggestion;
  /**
   * The desktop height, when a page has a header above the hero: the guest's page passes
   * `PUBLIC_LIST_HERO_HEIGHT`. Defaults to one viewport, the panel's.
   */
  height?: string;
};

export type HeroSuggestion = {
  name: string;
  titleLead: string;
  titleAccent: string;
  description: string;
  /** Null for a list without a photograph: the hero then wears the list's own cover. */
  coverUrl: string | null;
  /** "109 peças · 20 coleções". */
  countLabel: string;
};

/**
 * The event name broken over two lines: the "&" always opens the second name's line (v2 §6.1),
 * set in green.
 */
function EventTitle({ name }: { name: string }) {
  const parts = name.split("&");

  if (parts.length !== 2) return <>{name}</>;

  return (
    <>
      {/* The space before the break is not drawn (it ends the line) but keeps the heading's
          accessible name "A & B" rather than "A& B": on the guest's page it is the only H1. */}
      {parts[0].trim()}{" "}
      <br />
      <span className="text-[0.85em] text-tb-green">&amp;</span> {parts[1].trim()}
    </>
  );
}

export function CatalogHero({
  eventName,
  presentation,
  isClosed = false,
  suggestion,
  height = CATALOG_HERO_MIN_HEIGHT,
}: CatalogHeroProps) {
  const coverUrl = suggestion?.coverUrl ?? resolveCoverUrl(presentation.coverKey);
  const hasMessage = presentation.message.trim() !== "";
  const hasDate = presentation.eventDate.trim() !== "";
  const hasLocation = presentation.eventLocation.trim() !== "";
  const parallaxStageRef = useCatalogHeroParallax();

  return (
    <section
      /*
        A column that is **at least** one viewport tall on a desktop: the closed list's notice
        belongs inside this section, and a fixed height made it spill onto the card below (ruling
        of 2026-09-20). With the hero alone it is one viewport, as before.
      */
      className="box-border flex w-screen flex-col [margin-inline:calc(50%-50vw)] py-[clamp(22px,3vh,40px)] px-[clamp(20px,4vw,56px)] [@media(min-width:1024px)]:min-h-[var(--hero-h)]"
      style={{ "--hero-h": height } as CSSProperties}
    >
      {/*
        One grid, two arrangements. On a phone: the name, the photo, then the words under it. On a
        desktop: the name and the words in the left column, centred together between two flexible
        rows, and the cover the whole height of the right column.
      */}
      <div className="grid min-h-0 flex-none grid-cols-1 gap-x-[clamp(28px,4.5vw,64px)] [grid-template-areas:'head'_'cover'_'body'] [@media(min-width:1024px)]:h-[calc(var(--hero-h)-2*clamp(22px,3vh,40px))] [@media(min-width:1024px)]:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] [@media(min-width:1024px)]:grid-rows-[1fr_auto_auto_1fr] [@media(min-width:1024px)]:[grid-template-areas:'._cover'_'head_cover'_'body_cover'_'._cover']">
        <div className="flex min-w-0 flex-col [grid-area:head]">
          <SectionLabel tone="brand" rule={false} className="mb-0">
            {suggestion !== undefined ? "Lista de sugestão" : isClosed ? "Lista encerrada" : "Lista de Casamento"}
          </SectionLabel>

          <h1 className="mt-[30px] font-display text-[clamp(26px,7.5vw,32px)] font-light uppercase leading-[1.08] tracking-[-0.01em] [text-wrap:balance] [@media(min-width:1024px)]:text-[48px]">
            {suggestion !== undefined ? (
              <>
                {suggestion.titleLead}
                <br />
                <em className="not-italic text-tb-green">{suggestion.titleAccent}</em>
              </>
            ) : (
              <EventTitle name={eventName} />
            )}
          </h1>
        </div>

        {/* The first line of words sits 22px under what precedes it: the title on a desktop, the
            photo on a phone. The photo itself sits 28px under the title on a phone. */}
        <div className="flex min-w-0 flex-col [grid-area:body] pt-[22px]">
          {suggestion !== undefined ? (
            <>
              {/* The house's own words, not a host's message: no quotation marks, a shorter measure. */}
              <p className="max-w-[52ch] font-display text-[clamp(19px,1.7vw,22px)] font-light leading-[1.4] text-[var(--fg-muted)] [text-wrap:pretty]">
                {suggestion.description}
              </p>
              <span className="mt-[22px] text-[12px] uppercase tracking-[0.16em] text-[var(--fg-subtle)]">
                {suggestion.countLabel}
              </span>
            </>
          ) : (
            <>
              {/* What the list does not have is not drawn: no empty quotation marks, no pin without
                  a place, no separator between one thing and nothing. */}
              {hasMessage ? (
                <p className="max-w-[70ch] font-display text-[clamp(19px,1.7vw,22px)] font-light leading-[1.4] text-[var(--fg-muted)] [text-wrap:pretty]">
                  &#8220;{presentation.message}&#8221;
                </p>
              ) : null}

              {hasDate || hasLocation ? (
                <div
                  className={cn(
                    "flex flex-wrap items-center gap-3 text-[14px] leading-[1.55] text-[var(--fg-muted)]",
                    hasMessage && "mt-[26px]",
                  )}
                >
                  {hasDate ? (
                    <span className="inline-flex items-center gap-2">
                      <CalendarIcon className="size-[15px]" />
                      {formatCivilDateInFull(presentation.eventDate)}
                    </span>
                  ) : null}
                  {hasDate && hasLocation ? <span className="text-[var(--fg-subtle)]">·</span> : null}
                  {hasLocation ? (
                    <span className="inline-flex items-center gap-2">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d={LOCATION_ICON_PATH} />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {presentation.eventLocation}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/*
         * The 4:5 frame is the parallax boundary: fixed size, clipped overflow. The stage carries
         * only the scroll-driven translate, gated by `prefers-reduced-motion`; the 1.20 scale is a
         * permanent slack on the photo itself, so freezing motion never also un-zooms the crop.
         */}
        <div
          data-hero-cover=""
          className={cn(
            "relative mt-7 ml-auto aspect-[4/5] w-full max-w-full self-stretch overflow-hidden [grid-area:cover] [@media(min-width:1024px)]:h-full [@media(min-width:1024px)]:w-auto min-[1024px]:mt-0",
            coverUrl !== null ? "bg-[var(--tb-porcelain-tint)]" : "bg-[var(--tb-porcelain)]",
          )}
        >
          <div
            ref={parallaxStageRef}
            className="absolute inset-0 will-change-transform motion-reduce:!transform-none"
            style={{ transform: "translate3d(0, var(--heroPY, 0px), 0)" }}
          >
            {coverUrl !== null ? (
              <HeroCoverImage
                // A new address starts a new image: a failure of the old one must not carry over.
                key={coverUrl}
                src={coverUrl}
                // The prototype's alt joins the hosts with "e", where the title uses "&".
                alt={
                  suggestion !== undefined
                    ? `Capa da lista ${suggestion.name}`
                    : `Capa da lista de ${eventName.replace(" & ", " e ")}`
                }
              />
            ) : (
              // A list with no photo of its own wears the brand's cover (v2 §13). A static SVG:
              // nothing to optimise, so `unoptimized`, but it is the LCP like the photo is.
              <Image
                src={LIST_COVER_PLACEHOLDER_SRC}
                alt=""
                aria-hidden="true"
                fill
                unoptimized
                sizes={IMAGE_SIZES_PRESETS.listCover}
                loading="eager"
                fetchPriority="high"
                className="object-cover object-center"
              />
            )}
          </div>
        </div>
      </div>

      {isClosed ? (
        <div className="mt-[clamp(26px,3vw,36px)] flex items-start gap-4 bg-[var(--tb-porcelain)] p-[clamp(22px,2.6vw,30px)]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--tb-green)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="mt-0.5 flex-none"
          >
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
          <div className="flex min-w-0 flex-col gap-[7px]">
            <span className="font-display text-[18px] font-light uppercase leading-[1.15]">
              Esta lista foi encerrada
            </span>
            <span className="max-w-[60ch] text-[14px] leading-[1.65] text-[var(--fg-muted)] [text-wrap:pretty]">
              As peças seguem visíveis para consulta. O que foi presenteado já virou crédito,
              disponível no seu extrato.
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The cover photo, through `next/image` (PERF-02): sized by `sizes`, the first thing on screen
 * (`loading="eager"` and `fetchPriority="high"`, which the Next guide prefers to `preload` for an
 * image that is the LCP), at the quality the catalog reserves for photography that *is* the content.
 *
 * A host the optimiser is not configured for goes out as it is (`unoptimized`) instead of taking
 * the page down; a photograph that fails to load becomes the transparent pixel, same element and
 * same box, so the frame does not shift.
 */
function HeroCoverImage({ src, alt }: { src: string; alt: string }) {
  const [hasFailed, setHasFailed] = useState(false);
  const shown = hasFailed ? TRANSPARENT_PIXEL : src;

  return (
    <Image
      src={shown}
      alt={alt}
      fill
      sizes={IMAGE_SIZES_PRESETS.listCover}
      quality={IMAGE_QUALITY.high}
      loading="eager"
      fetchPriority="high"
      unoptimized={hasFailed || !isOptimizableImageSrc(src)}
      onError={() => setHasFailed(true)}
      className="scale-[1.2] object-cover object-center"
    />
  );
}
